import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import ModuleShell from '../../components/ModuleShell';
import {
  normalizeEconomicsScenario,
  useEconomicsScenario,
} from './useEconomicsScenario';
import { useScenarioLibrary } from '../opsLibrary/useScenarioLibrary';
import LibraryPanel from '../opsLibrary/LibraryPanel';
import RatesPanel from './components/RatesPanel';
import PlansPanel from './components/PlansPanel';
import ProfilesPanel from './components/ProfilesPanel';
import GrowthPanel from './components/GrowthPanel';
import ResultsPanel from './components/ResultsPanel';
import ActualsPanel from './components/ActualsPanel';

const TABS = [
  { id: 'rates', label: 'Rates & plans' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'growth', label: 'Scale drivers' },
  { id: 'results', label: 'Results' },
  { id: 'actuals', label: 'Actuals' },
  { id: 'library', label: 'Library' },
];

export default function EconomicsModule() {
  const [activeTab, setActiveTab] = useState('rates');
  const {
    scenario,
    setScenario,
    projection,
    resetToDefaults,
    applyGrowthPreset,
    exportJson,
    exportCsv,
  } = useEconomicsScenario();

  const applyPayload = useCallback(
    (payload) => {
      setScenario(normalizeEconomicsScenario(payload));
    },
    [setScenario]
  );

  const library = useScenarioLibrary('economics', {
    getPayload: () => scenario,
    applyPayload,
  });

  const patchScenario = (patch) => {
    setScenario((prev) => ({ ...prev, ...patch }));
  };

  const handleReset = () => {
    if (window.confirm('Reset all economics assumptions to defaults?')) {
      resetToDefaults();
      library.clearActiveLink();
    }
  };

  return (
    <ModuleShell
      title="Economics"
      description={
        <>
          <p>
            Unit economics / Plaid COGS modeler — stress PAYG rates, usage profiles, and scale
            drivers. Assumptions-only; working copy in this browser. For MRR planning use{' '}
            <Link to="/forecast">Forecast</Link>.
          </p>
          <p className="chart-hint">
            As of {scenario.asOf} · Plaid mode:{' '}
            {scenario.plaidMode === 'growth_platform' ? 'Growth platform ($2k/mo)' : 'Pay As You Go'}
            {library.activeMeta?.libraryName
              ? ` · Saved as “${library.activeMeta.libraryName}”`
              : ''}
          </p>
        </>
      }
      actions={
        <>
          <button type="button" className="secondary-button" onClick={exportJson}>
            Export JSON
          </button>
          <button type="button" className="secondary-button" onClick={exportCsv}>
            Export CSV
          </button>
          <button type="button" className="secondary-button" onClick={handleReset}>
            Reset defaults
          </button>
        </>
      }
    >
      <nav className="economics-tabs" aria-label="Economics sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={
              activeTab === tab.id ? 'economics-tab economics-tab-active' : 'economics-tab'
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'rates' && (
        <div className="economics-tab-panel">
          <RatesPanel
            rates={scenario.rates}
            onChange={(rates) => patchScenario({ rates })}
          />
          <PlansPanel
            plans={scenario.plans}
            onChange={(plans) => patchScenario({ plans })}
          />
        </div>
      )}

      {activeTab === 'profiles' && (
        <ProfilesPanel
          profiles={scenario.profiles}
          plans={scenario.plans}
          rates={scenario.rates}
          onChange={(profiles) => patchScenario({ profiles })}
        />
      )}

      {activeTab === 'growth' && (
        <GrowthPanel
          growth={scenario.growth}
          profiles={scenario.profiles}
          onChange={(growth) => patchScenario({ growth })}
          onApplyPreset={applyGrowthPreset}
        />
      )}

      {activeTab === 'results' && (
        <ResultsPanel
          projection={projection}
          profiles={scenario.profiles}
          scenario={scenario}
          onPlaidModeChange={(plaidMode) => patchScenario({ plaidMode })}
        />
      )}

      {activeTab === 'actuals' && (
        <ActualsPanel projection={projection} rates={scenario.rates} />
      )}

      {activeTab === 'library' && (
        <LibraryPanel
          kindLabel="Economics"
          entries={library.entries}
          activeMeta={library.activeMeta}
          message={library.message}
          onDismissMessage={() => library.setMessage(null)}
          onSaveAs={({ name, notes }) => library.saveAs({ name, notes })}
          onOverwrite={({ id, name, notes }) => library.saveAs({ id, name, notes })}
          onLoad={library.loadEntry}
          onDelete={library.removeEntry}
          onDownloadLibrary={library.downloadLibrary}
          onUploadLibrary={library.uploadLibrary}
        />
      )}
    </ModuleShell>
  );
}
